// @flow
import * as React from 'react';
import {
  Form, RadioGroup, Radio, TextArea,
} from 'informed';
import moment from 'moment';

import {
  PrimaryText, HeaderText, Error,
} from '../../Typography.jsx';
import { Button, PrimaryButton } from '../../Buttons.jsx';
import DateText from '../../DateText.jsx';

import styles from '../../../styles/patientPages/testing/Urinalysis.scss';

export const normalValues = {
  leu: '-',
  nit: '-',
  uro: '0.2 (3.5)',
  pro: '-',
  ph: '5.0',
  blo: '-',
  sg: '1.000',
  ket: '-',
  bil: '-',
  glu: '-',
};

type Props = {
  editRow: ?Object,
  // if view only, no fields editable
  viewOnly: bool,
  /*
    (rowId: string, row: Object) => {}
  */
  handleSaveRow: Function,
  /*
    (row: Object) => {}
  */
  handleSaveNewRow: Function,
  closeModal: Function,
}

class TBScreeningForm extends React.Component<Props, {}> {
  formApi: {
    getState: Function,
    setValue: Function,
    reset: Function,
  }

  componentDidUpdate() {
    this.formApi.reset();
    if (this.props.editRow && !this.props.viewOnly) {
      this.formApi.setValue('date', moment(this.props.editRow.date).format());
    } else if (!this.props.viewOnly) {
      this.formApi.setValue('date', moment().format());
    }
  }

  setFormApi = (formApi: Object) => {
    this.formApi = formApi;
  }

  handleSave = () => {
    const state = this.formApi.getState();
    if (state.invalid) {
      return;
    }
    if (!this.props.editRow) {
      this.props.handleSaveNewRow(state);
    } else if (Object.keys(state.touched).length) {
      this.props.handleSaveRow(this.props.editRow._id, state);
    }
    this.props.closeModal();
  }

  /* eslint-disable max-len */
  render() {
    const { viewOnly } = this.props;
    const editRow = this.props.editRow || {};
    const { editors, editDates } = editRow;
    let editHistory = '';
    if (editors) {
      for (let i = 0; i < editors.length; i++) {
        if (i === editors.length - 1) {
          editHistory += `${moment(editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${editors[i]} (created) \n`;
        } else {
          editHistory += `${moment(editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${editors[i]}\n`;
        }
      }
    }

    return (
      <Form getApi={this.setFormApi} autoComplete='off'>
        <HeaderText>Urinalysis Test</HeaderText>
        <PrimaryText>Results</PrimaryText>
        <table className={styles.urinalysisResults}>
          <tbody>
            <tr>
              <td>Leukocytes (Leu/μL)</td>
              <td>{
                viewOnly
                  ? editRow.leu === normalValues.leu ? editRow.leu : <Error>{editRow.leu}</Error>
                  : <RadioGroup field='leu' initialValue={editRow.leu}>
                    <Radio value={'-'} />-
                    <br />
                    <Radio value={'15 ±'} />15 ±
                    <br />
                    <Radio value={'70 +'} />70 +
                    <br />
                    <Radio value={'125 ++'} />125 ++
                    <br />
                    <Radio value={'500 +++'} />500 +++
                  </RadioGroup>}
              </td>
            </tr>
            <tr>
              <td>Nitrite</td>
              <td>{
                viewOnly
                  ? editRow.nit === normalValues.nit ? editRow.nit : <Error>{editRow.nit}</Error>
                  : <RadioGroup field='nit' initialValue={editRow.nit}>
                    <Radio value={'-'} />-
                    <br />
                    <Radio value={'+'} />+
                  </RadioGroup>}
              </td>
            </tr>
            <tr>
              <td>Urobilinogen (mg/dL(μmol/L))</td>
              <td>{
                viewOnly
                  ? editRow.uro === normalValues.uro ? editRow.uro : <Error>{editRow.uro}</Error>
                  : <RadioGroup field='uro' initialValue={editRow.uro}>
                    <Radio value={'0.2 (3.5)'} />0.2 (3.5)
                    <br />
                    <Radio value={'1 (17)'} />1 (17)
                    <br />
                    <Radio value={'2 (35)'} />2 (35)
                    <br />
                    <Radio value={'4 (70)'} />4 (70)
                    <br />
                    <Radio value={'8 (140)'} />8 (140)
                    <br />
                    <Radio value={'12 (200)'} />12 (200)
                  </RadioGroup>}
              </td>
            </tr>
            <tr>
              <td>Protein (mg/dL(g/L))</td>
              <td>{
                viewOnly
                  ? editRow.pro === normalValues.pro ? editRow.pro : <Error>{editRow.pro}</Error>
                  : <RadioGroup field='pro' initialValue={editRow.pro}>
                    <Radio value={'-'} />-
                    <br />
                    <Radio value={'15 (0.15) ±'} />15 (0.15) ±
                    <br />
                    <Radio value={'300 (0.3) +'} />300 (0.3) +
                    <br />
                    <Radio value={'100 (1.0) ++'} />100 (1.0) ++
                    <br />
                    <Radio value={'300 (3.0) +++'} />300 (3.0) +++
                    <br />
                    <Radio value={'2000 (20) ++++'} />2000 (20) ++++
                  </RadioGroup>}
              </td>
            </tr>
            <tr>
              <td>pH</td>
              <td>{
                viewOnly
                  ? editRow.ph === normalValues.ph ? editRow.ph : <Error>{editRow.ph}</Error>
                  : <RadioGroup field='ph' initialValue={editRow.ph}>
                    <Radio value={'5.0'} />5.0
                    <br />
                    <Radio value={'6.0'} />6.0
                    <br />
                    <Radio value={'6.5'} />6.5
                    <br />
                    <Radio value={'7.0'} />7.0
                    <br />
                    <Radio value={'7.5'} />7.5
                    <br />
                    <Radio value={'8.0'} />8.0
                    <br />
                    <Radio value={'9.0'} />9.0
                  </RadioGroup>}
              </td>
            </tr>
            <tr>
              <td>Blood (Ery/μL)</td>
              <td>{
                viewOnly
                  ? editRow.blo === normalValues.blo ? editRow.blo : <Error>{editRow.blo}</Error>
                  : <RadioGroup field='blo' initialValue={editRow.blo}>
                    <Radio value={'-'} />-
                    <br />
                    <Radio value={'±'} />±
                    <br />
                    <Radio value={'+'} />+
                    <br />
                    <Radio value={'++'} />++
                    <br />
                    <Radio value={'+++'} />+++
                    <br />
                    <Radio value={'5-10'} />5-10
                    <br />
                    <Radio value={'50'} />50
                  </RadioGroup>}
              </td>
            </tr>
            <tr>
              <td>Specific Gravity</td>
              <td>{
                viewOnly
                  ? editRow.sg === normalValues.sg ? editRow.sg : <Error>{editRow.sg}</Error>
                  : <RadioGroup field='sg' initialValue={editRow.sg}>
                    <Radio value={'1.000'} />1.000
                    <br />
                    <Radio value={'1.005'} />1.005
                    <br />
                    <Radio value={'1.010'} />1.010
                    <br />
                    <Radio value={'1.015'} />1.015
                    <br />
                    <Radio value={'1.020'} />1.020
                    <br />
                    <Radio value={'1.025'} />1.025
                    <br />
                    <Radio value={'1.030'} />1.030
                  </RadioGroup>}
              </td>
            </tr>
            <tr>
              <td>Ketones (mg/dL(mmol/L))</td>
              <td>{
                viewOnly
                  ? editRow.ket === normalValues.ket ? editRow.ket : <Error>{editRow.ket}</Error>
                  : <RadioGroup field='ket' initialValue={editRow.ket}>
                    <Radio value={'-'} />-
                    <br />
                    <Radio value={'5 (0.5) ±'} />5 (0.5) ±
                    <br />
                    <Radio value={'15 (1.5) +'} />15 (1.5) +
                    <br />
                    <Radio value={'40 (4.0) ++'} />40 (4.0) ++
                    <br />
                    <Radio value={'80 (8.0) +++'} />80 (8.0) +++
                    <br />
                    <Radio value={'160 (16) ++++'} />160 (16) ++++
                  </RadioGroup>}
              </td>
            </tr>
            <tr>
              <td>Bilirubin (mg/dL(μmol/L))</td>
              <td>{
                viewOnly
                  ? editRow.bil === normalValues.bil ? editRow.bil : <Error>{editRow.bil}</Error>
                  : <RadioGroup field='bil' initialValue={editRow.bil}>
                    <Radio value={'-'} />-
                    <br />
                    <Radio value={'1 (17) +'} />1 (17) +
                    <br />
                    <Radio value={'2 (35) ++'} />2 (35) ++
                    <br />
                    <Radio value={'4 (70) +++'} />4 (70) +++
                  </RadioGroup>}
              </td>
            </tr>
            <tr>
              <td>Glucose (mg/dL(mmol/L))</td>
              <td>{
                viewOnly
                  ? editRow.glu === normalValues.glu ? editRow.glu : <Error>{editRow.glu}</Error>
                  : <RadioGroup field='glu' initialValue={editRow.glu}>
                    <Radio value={'-'} />-
                    <br />
                    <Radio value={'100 (5) ±'} />100 (5) ±
                    <br />
                    <Radio value={'250 (15) +'} />250 (15) +
                    <br />
                    <Radio value={'500 (30) ++'} />500 (30) ++
                    <br />
                    <Radio value={'1000 (60) +++'} />1000 (60) +++
                    <br />
                    <Radio value={'≥2000 (110) ++++'} />≥2000 (110) ++++
                  </RadioGroup>}
              </td>
            </tr>
          </tbody>
        </table>
        <br />
        <div className={styles.notes}>
          <PrimaryText>Notes</PrimaryText><br />
          {viewOnly
            ? editRow.notes || '--'
            : <TextArea field='notes' initialValue={editRow.notes} />
          }
        </div>
        <table>
          <tbody>
            <tr>
              <td><PrimaryText>Exam Date </PrimaryText></td>
              <td>
                {viewOnly
                  ? (editRow.date ? moment(editRow.date).format() : '--')
                  : <DateText field='date' item={editRow} />
                }
              </td>
            </tr>
            <tr className={styles.editHistoryRow}>
              <td><strong>Edit History </strong></td>
              <td>{editHistory || '--'}</td>
            </tr>
          </tbody>
        </table>
        {!viewOnly && <PrimaryButton onClick={this.handleSave}>Save</PrimaryButton>}
        <Button onClick={this.props.closeModal}>{viewOnly ? 'Close' : 'Cancel'}</Button>
      </Form>
    );
  }
}

export default TBScreeningForm;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/testing/UrinalysisForm.jsx